import { useState, useRef, useEffect, ReactNode } from 'react';
import { MoreVertical, MoreHorizontal } from 'lucide-react';

export interface ActionMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
  divider?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  trigger?: 'vertical' | 'horizontal' | ReactNode;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
  className?: string;
}

const variantStyles = {
  default: 'text-gray-700 hover:bg-gray-50',
  danger: 'text-red-600 hover:bg-red-50',
  success: 'text-green-600 hover:bg-green-50',
  warning: 'text-orange-600 hover:bg-orange-50',
};

export default function ActionMenu({
  items,
  trigger = 'vertical',
  align = 'right',
  size = 'md',
  className = '',
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate menu position based on available space using fixed positioning
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const menuHeight = 200; // Approximate menu height
      const menuWidth = 160; // Approximate menu width

      const newStyle: { top?: number; bottom?: number; left?: number; right?: number } = {};

      // Vertical position
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        newStyle.bottom = window.innerHeight - buttonRect.top + 4;
      } else {
        newStyle.top = buttonRect.bottom + 4;
      }

      // Horizontal position
      if (align === 'right') {
        newStyle.right = window.innerWidth - buttonRect.right;
      } else {
        newStyle.left = buttonRect.left;
        // Make sure menu doesn't go off-screen
        if (buttonRect.left + menuWidth > window.innerWidth) {
          newStyle.left = window.innerWidth - menuWidth - 8;
        }
      }

      setMenuStyle(newStyle);
    }
  }, [isOpen, align]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item: ActionMenuItem) => {
    if (item.disabled) return;
    item.onClick();
    setIsOpen(false);
  };

  const visibleItems = items.filter((item) => !item.hidden);

  if (visibleItems.length === 0) return null;

  const iconSize = size === 'sm' ? 16 : 18;
  const buttonPadding = size === 'sm' ? 'p-1' : 'p-1.5';

  const renderTrigger = () => {
    if (typeof trigger === 'string') {
      const Icon = trigger === 'vertical' ? MoreVertical : MoreHorizontal;
      return <Icon size={iconSize} />;
    }
    return trigger;
  };

  return (
    <div ref={menuRef} className={`relative inline-block ${className}`}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`${buttonPadding} text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
        aria-label="More options"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {renderTrigger()}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div
            className="fixed z-[9999] min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-100 py-1 animate-in fade-in-0 zoom-in-95 duration-100"
            style={menuStyle}
          >
            {visibleItems.map((item, index) => (
              <div key={index}>
                {item.divider && index > 0 && (
                  <div className="border-t border-gray-100 my-1" />
                )}
                <button
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                    variantStyles[item.variant || 'default']
                  } ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  {item.icon && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      {item.icon}
                    </span>
                  )}
                  <span>{item.label}</span>
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
